import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, CheckCircle2, Award, Quote } from "lucide-react";
import Layout from "@/components/site/Layout";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import { useSite } from "@/context/SiteContext";
import iconFor from "@/components/site/iconMap";
import api, { mediaUrl } from "@/lib/api";

export default function Home() {
  const { content } = useSite();
  const [data, setData] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/content/solutions"), api.get("/content/industries"), api.get("/content/projects"),
      api.get("/content/oem_partners"), api.get("/content/testimonials"), api.get("/content/certifications"),
    ]).then(([s, i, p, o, t, c]) =>
      setData({ solutions: s.data, industries: i.data, projects: p.data, oem: o.data, testimonials: t.data, certs: c.data })
    ).catch(() => {});
  }, []);

  useEffect(() => {
    if (content?.seo) {
      document.title = content.seo.title || "Labpronics Technologies";
    }
  }, [content]);

  if (!content) return <div className="min-h-screen bg-lp-grey" />;
  const hero = content.hero || {};
  const about = content.about || {};

  return (
    <Layout>
      {/* HERO */}
      <section className="relative pt-[74px] bg-lp-grey overflow-hidden">
        <div className="container-lp grid lg:grid-cols-12 gap-12 items-center py-16 lg:py-24">
          <div className="lg:col-span-6">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow mb-5">{hero.eyebrow}</motion.p>
            <h1 className="font-heading font-700 text-lp-blue leading-[1.02] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              {(hero.title_lines || []).map((l, i) => <MaskLine key={i} delay={0.15 + i * 0.15}>{l}</MaskLine>)}
              {hero.highlight && <MaskLine delay={0.15 + (hero.title_lines || []).length * 0.15}><span className="text-lp-cyan">{hero.highlight}</span></MaskLine>}
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }} className="mt-6 text-lp-muted text-lg leading-relaxed max-w-xl">{hero.subtitle}</motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }} className="mt-8 flex flex-wrap gap-4">
              <Link to={hero.cta_primary?.link || "/solutions"} data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-7 py-3.5 rounded-sm hover:bg-lp-steel transition-colors group">
                {hero.cta_primary?.label || "Explore Solutions"} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={hero.cta_secondary?.link || "/projects"} data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-lp-blue text-lp-blue font-600 px-7 py-3.5 rounded-sm hover:bg-lp-blue hover:text-white transition-colors">
                {hero.cta_secondary?.label || "View Projects"}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <div className="img-zoom clip-frame border border-lp-border shadow-[0_30px_60px_-30px_rgba(10,43,110,0.4)]">
                <img src={hero.image} alt="Command and control monitoring centre" className="w-full h-[440px] object-cover" />
              </div>
              {hero.badge_value && (
                <div className="absolute -bottom-5 -left-5 bg-lp-blue text-white px-6 py-4 rounded-sm shadow-lg hidden sm:block">
                  <div className="font-heading font-700 text-2xl">{hero.badge_value}</div>
                  <div className="text-xs text-white/70 tracking-wide">{hero.badge_label}</div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
        <div className="border-t border-lp-border bg-white">
          <div className="container-lp grid grid-cols-2 md:grid-cols-4 divide-x divide-lp-border">
            {(content.stats || []).map((s, i) => (
              <Reveal key={i} delay={i * 0.08} className="py-7 px-4 text-center">
                <div className="font-heading font-700 text-lp-blue text-2xl md:text-3xl">{s.value}</div>
                <div className="text-xs md:text-sm text-lp-muted font-600 mt-1 tracking-wide">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-lp-blue py-4">
        <Marquee speed={40} gradient={false} autoFill>
          {(content.marquee || []).map((t, i) => (
            <span key={i} className="mx-8 font-heading font-600 tracking-[0.15em] uppercase text-white/85 text-sm flex items-center gap-8">{t} <span className="text-lp-cyan">/</span></span>
          ))}
        </Marquee>
      </div>

      {/* ABOUT */}
      <section className="py-24">
        <div className="container-lp grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="img-zoom clip-frame border border-lp-border">
              <img src={about.image} alt="Engineers in a control room" loading="lazy" className="w-full h-[460px] object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal><p className="eyebrow mb-4">{about.eyebrow}</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl leading-tight tracking-tight">{about.heading}</h2></Reveal>
            <Reveal delay={0.1}><div className="mt-6 space-y-4 text-lp-muted leading-relaxed">{(about.body || []).map((p, i) => <p key={i}>{p}</p>)}</div></Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 grid grid-cols-3 gap-4">
                {(Array.isArray(about.tiles) ? about.tiles : []).map((t) => (
                  <div key={t} className="border border-lp-border rounded-sm p-4 text-center hover:border-lp-cyan transition-colors">
                    <CheckCircle2 className="mx-auto text-lp-cyan mb-2" size={22} />
                    <div className="text-xs font-600 text-lp-ink">{t}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-24 bg-lp-grey">
        <div className="container-lp">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow mb-4">What We Do</p>
            <h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">Solutions Across Six Core Domains</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(data.solutions || []).map((s, i) => {
              const Icon = iconFor(s.icon);
              return (
                <Reveal key={s.id} delay={(i % 3) * 0.08}>
                  <Link to="/solutions" data-testid={`solution-card-${i}`} className="group bg-white border border-lp-border rounded-sm overflow-hidden hover:border-lp-cyan hover:shadow-[0_20px_40px_-24px_rgba(10,43,110,0.35)] transition-all duration-300 h-full flex flex-col">
                    <div className="img-zoom h-44 relative">
                      <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-lp-blue/20 group-hover:bg-lp-blue/5 transition-colors" />
                      <div className="absolute bottom-3 left-3 h-11 w-11 bg-white rounded-sm flex items-center justify-center shadow"><Icon size={22} className="text-lp-blue" /></div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading font-700 text-lp-blue text-xl leading-snug">{s.title}</h3>
                      <p className="mt-3 text-sm text-lp-muted leading-relaxed flex-1">{s.excerpt}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-600 text-lp-cyan">Learn More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24">
        <div className="container-lp">
          <Reveal className="max-w-2xl mb-14"><p className="eyebrow mb-4">Sectors We Serve</p><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">Industries</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {(data.industries || []).map((ind, i) => {
              const Icon = iconFor(ind.icon);
              return (
                <Reveal key={ind.id} delay={(i % 3) * 0.08}>
                  <Link to="/industries" className="group border border-lp-border rounded-sm p-8 flex items-center gap-5 hover:bg-lp-blue transition-colors duration-300 h-full">
                    <Icon size={30} className="text-lp-cyan shrink-0" />
                    <span className="font-heading font-600 text-lg text-lp-ink group-hover:text-white transition-colors">{ind.name}</span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="py-24 bg-lp-blue text-white">
        <div className="container-lp">
          <div className="flex items-end justify-between mb-14 gap-6">
            <Reveal className="max-w-2xl"><p className="eyebrow mb-4">Deployments</p><h2 className="font-heading font-700 text-3xl md:text-4xl tracking-tight">Deployments Across India</h2></Reveal>
            <Reveal><Link to="/projects" className="hidden sm:inline-flex items-center gap-2 border border-white/30 px-5 py-2.5 rounded-sm font-600 text-sm hover:bg-white hover:text-lp-blue transition-colors">All Projects <ArrowRight size={15} /></Link></Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(data.projects || []).slice(0, 4).map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.07}>
                <Link to="/projects" className="group block border border-white/20 rounded-sm overflow-hidden hover:border-lp-cyan transition-colors h-full">
                  <div className="img-zoom h-40"><img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <span className="text-[11px] font-600 tracking-wide text-lp-cyan uppercase">{p.location}</span>
                    <h3 className="font-heading font-600 text-lg mt-1.5">{p.title}</h3>
                    <p className="text-sm text-white/65 mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MANIFESTO */}
      <section className="py-24">
        <div className="container-lp">
          <Reveal className="max-w-2xl mb-14"><p className="eyebrow mb-4">Why Labpronics</p><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">Why Institutions Choose Us</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-lp-border border border-lp-border rounded-sm overflow-hidden">
            {(content.why || []).map((m, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="bg-white p-9 h-full group hover:bg-lp-grey transition-colors">
                  <span className="font-heading font-700 text-4xl text-lp-border group-hover:text-lp-cyan transition-colors block mb-5">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-heading font-600 text-xl text-lp-blue">{m.title}</h3>
                  <p className="mt-3 text-sm text-lp-muted leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OEM PARTNERS */}
      <section className="py-20 bg-lp-grey">
        <div className="container-lp">
          <Reveal className="text-center mb-12"><p className="eyebrow mb-4">Technology Alliances</p><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">{content.oem_heading || "Authorised OEM Partnerships"}</h2></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {(data.oem || []).slice(0, 15).map((o, i) => (
              <Reveal key={o.id} delay={(i % 5) * 0.04}>
                <div className="bg-white border border-lp-border rounded-sm h-24 flex items-center justify-center p-5 hover:border-lp-cyan transition-colors">
                  {o.logo ? <img src={mediaUrl(o.logo)} alt={o.name} loading="lazy" className="max-h-10 max-w-full object-contain" onError={(e) => { e.target.style.display = "none"; }} /> : <span className="font-heading font-700 text-lp-blue text-base text-center">{o.name}</span>}
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10"><Link to="/oem-partners" className="inline-flex items-center gap-2 text-lp-cyan font-600">View all partners <ArrowRight size={15} /></Link></div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {(data.testimonials || []).length > 0 && (
        <section className="py-24">
          <div className="container-lp">
            <Reveal className="max-w-2xl mb-14"><p className="eyebrow mb-4">Client Confidence</p><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">{content.testimonials_heading || "Trusted by Institutions"}</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {(data.testimonials || []).map((t, i) => (
                <Reveal key={t.id} delay={(i % 3) * 0.08}>
                  <div className="border border-lp-border rounded-sm p-8 h-full flex flex-col">
                    <Quote size={30} className="text-lp-cyan mb-4" />
                    <p className="text-lp-ink leading-relaxed flex-1">"{t.quote}"</p>
                    <div className="mt-6 pt-5 border-t border-lp-border">
                      <div className="font-heading font-600 text-lp-blue">{t.name}</div>
                      <div className="text-sm text-lp-muted">{t.role}{t.org ? `, ${t.org}` : ""}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CERTIFICATIONS */}
      <section className="py-20 bg-lp-grey">
        <div className="container-lp">
          <Reveal className="text-center mb-12"><p className="eyebrow mb-4">Accreditations</p><h2 className="font-heading font-700 text-lp-blue text-3xl md:text-4xl tracking-tight">Certifications &amp; Compliance</h2></Reveal>
          <div className="flex flex-wrap justify-center gap-4">
            {(data.certs || []).map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05}>
                <div className="bg-white border border-lp-border rounded-sm px-7 py-5 flex items-center gap-3 hover:border-lp-cyan transition-colors">
                  <Award size={22} className="text-lp-cyan" /><span className="font-heading font-600 text-lp-ink">{c.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      {content.cta_band && (
        <section className="py-20 bg-lp-blue text-white">
          <div className="container-lp text-center">
            <Reveal>
              <h2 className="font-heading font-700 text-3xl md:text-4xl tracking-tight max-w-3xl mx-auto">{content.cta_band.heading}</h2>
              <p className="mt-5 text-white/75 max-w-2xl mx-auto">{content.cta_band.text}</p>
              <Link to={content.cta_band.button?.link || "/contact"} className="mt-8 inline-flex items-center gap-2 bg-lp-cyan text-lp-blue font-700 px-8 py-3.5 rounded-sm hover:bg-white transition-colors">
                {content.cta_band.button?.label || "Get in Touch"} <ArrowRight size={17} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}
    </Layout>
  );
}
